import { useEffect, useState } from "react";
import { ArrowLeft, Play } from "lucide-react";
import { Button, IconButton } from "@/sentinel";

// Soft walkthrough. Highlights each target element directly (focus-ring outline) so the focus
// is always aligned, with a fixed card that explains the step. Non-blocking.
const HL = "sentinel-tour-highlight";

function clearHighlight() {
  document.querySelectorAll("." + HL).forEach((el) => {
    el.classList.remove(HL);
    el.style.outline = "";
    el.style.outlineOffset = "";
    el.style.borderRadius = el.dataset._tourbr || el.style.borderRadius;
    el.style.boxShadow = el.dataset._tourbs || "";
    el.style.position = el.dataset._tourpos || "";
    el.style.zIndex = el.dataset._tourz || "";
  });
}

export default function GuidedTour({ steps, onRun, onClose }) {
  const [i, setI] = useState(0);
  const [running, setRunning] = useState(false);
  const step = steps[i];

  useEffect(() => {
    clearHighlight();
    if (step?.testid) {
      const el = document.querySelector(`[data-testid="${step.testid}"]`);
      if (el) {
        el.classList.add(HL);
        el.dataset._tourbs = el.style.boxShadow;
        el.dataset._tourpos = el.style.position;
        el.dataset._tourz = el.style.zIndex;
        el.style.outline = "var(--focus-width) solid var(--focus-ring)";
        el.style.outlineOffset = "3px";
        el.style.boxShadow = "0 0 0 6px var(--brand-dim)";
        if (!el.style.position || el.style.position === "static") el.style.position = "relative";
        el.style.zIndex = "95";
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }
    return clearHighlight;
  }, [step]);

  const finish = () => { clearHighlight(); onClose(); };
  const next = () => (i < steps.length - 1 ? setI(i + 1) : finish());
  const back = () => setI(Math.max(0, i - 1));
  const runNow = async () => {
    setRunning(true);
    try { await onRun?.(); } finally { setRunning(false); setTimeout(() => setI((x) => Math.min(x + 1, steps.length - 1)), 300); }
  };

  return (
    <div data-testid="guided-tour" style={{ position: "fixed", inset: 0, zIndex: 90, pointerEvents: "none" }}>
      <div style={{ position: "fixed", inset: 0, background: "var(--surface-scrim)", opacity: 0.5, pointerEvents: "none" }} />
      <section className="sn-panel" data-testid="tour-card" role="dialog" aria-label={`Guided tour, step ${i + 1} of ${steps.length}`}
        style={{ position: "fixed", right: 24, bottom: 24, width: 340, maxWidth: "calc(100vw - 32px)", zIndex: 96, pointerEvents: "auto",
          background: "var(--surface-overlay)", borderColor: "var(--border-strong)", boxShadow: "var(--shadow-overlay)", padding: "var(--space-4)" }}>
        <div className="sn-row" style={{ justifyContent: "space-between", flexWrap: "nowrap", marginBottom: "var(--space-1)" }}>
          <span className="sn-micro">Step <span className="sn-num">{i + 1}</span> of <span className="sn-num">{steps.length}</span></span>
          <IconButton icon="X" label="Skip tour" size="sm" onClick={finish} data-testid="tour-skip" />
        </div>
        <h2 style={{ margin: "0 0 var(--space-1)", fontSize: 14, lineHeight: "20px", fontWeight: 600 }}>{step.title}</h2>
        <p className="sn-muted" style={{ margin: "0 0 var(--space-3)", fontSize: 13, lineHeight: "20px" }}>{step.body}</p>
        <div className="sn-row">
          {step.run ? (
            <Button variant="primary" size="sm" icon={Play} onClick={runNow} loading={running} data-testid="tour-run">
              {running ? "Running" : "Run it now"}
            </Button>
          ) : (
            <Button variant="primary" size="sm" iconRight={i < steps.length - 1 ? "ArrowRight" : undefined} onClick={next} data-testid="tour-next">
              {i < steps.length - 1 ? "Next" : "Done"}
            </Button>
          )}
          {i > 0 && <IconButton icon={ArrowLeft} label="Previous step" size="sm" variant="secondary" onClick={back} data-testid="tour-back" />}
          {step.run && <Button size="sm" variant="ghost" onClick={next} data-testid="tour-skip-step">Skip</Button>}
        </div>
      </section>
    </div>
  );
}
